import { FC } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { ListItem, UnorderedList } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  TaxForecastProfileFamilySituation,
  TaxForecastProfileTaxCalculationMode,
} from '__generated__/GQL';
import Accordion from 'components/_core/Accordion';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';
import { UpdateTaxDataDocument } from 'features/Accounting/pages/Estimation/graphql/updateTaxData.gql';

import StepLayout from '../../layout/Step';
import { StepComponentProps } from '../types';
import PersonalizedCard from './components/PersonalizedCard';
import StandardCard from './components/StandardCard';
import {
  formatTaxTypeInput,
  NeutralTaxTypeInput,
  PersonalizedTaxTypeInput,
  StandardTaxTypeInput,
} from './helpers/formatTaxTypeInput';
import { locales } from './locales';
import { TaxTypeSchema, taxTypeSchema } from './taxType.schema';

type SavePayload =
  | StandardTaxTypeInput
  | PersonalizedTaxTypeInput
  | NeutralTaxTypeInput;

const TaxType: FC<StepComponentProps<SavePayload>> = ({
  context,
  onBack,
  onNext,
  onSave,
}) => {
  const { showToast } = useToast();
  const { companyProfile, stepProgress, taxForecastProfile } = context;

  const [updateTaxData, { loading }] = useMutation(UpdateTaxDataDocument, {
    onError: () => showToast({ message: locales.error, type: 'error' }),
  });

  /**
   * Default values are derived from URSSAF documentation
   * @link https://mon-entreprise.urssaf.fr/documentation/imp%C3%B4t
   */
  const form = useForm<TaxTypeSchema>({
    defaultValues: {
      additionalHouseholdRevenue: taxForecastProfile?.familyAdditionalRevenue,
      customRate: taxForecastProfile?.customTaxRate,
      familySituation:
        taxForecastProfile?.familySituation ??
        TaxForecastProfileFamilySituation.Single,
      isSingleParent: taxForecastProfile?.isAloneWithChild ?? false,
      numberOfChildren: taxForecastProfile?.numberOfChildren,
      taxCalculationMode:
        taxForecastProfile?.taxCalculationMode ??
        TaxForecastProfileTaxCalculationMode.Standard,
    },
    resolver: zodResolver(taxTypeSchema),
  });

  const handleSave: SubmitHandler<TaxTypeSchema> = async (values) => {
    const formattedInput = formatTaxTypeInput(values);

    await updateTaxData({
      onCompleted: () => {
        onSave(formattedInput);
        onNext();
      },
      variables: {
        input: {
          companyProfileId: companyProfile.companyProfileId,
          taxForecastProfileData: formattedInput,
          taxFrequency: companyProfile.taxFrequency,
        },
      },
    });
  };

  const selectedTaxMode = form.watch('taxCalculationMode');

  return (
    <StepLayout>
      <StepLayout.Header onBack={onBack} stepProgress={stepProgress} />

      <StepLayout.Body>
        <Heading marginBottom="space-32">{locales.title}</Heading>

        <FormProvider {...form}>
          <Controller
            control={form.control}
            name="taxCalculationMode"
            render={({ field: { onChange, value } }) => (
              <SunshineCard.RadioGroup onChange={onChange} value={value}>
                <SunshineCard
                  isActive={
                    selectedTaxMode ===
                    TaxForecastProfileTaxCalculationMode.Neutral
                  }
                  title={locales.neutral}
                  value={TaxForecastProfileTaxCalculationMode.Neutral}
                  variant="radio"
                />

                <PersonalizedCard
                  isSelected={
                    selectedTaxMode ===
                    TaxForecastProfileTaxCalculationMode.Custom
                  }
                />

                <StandardCard
                  isSelected={
                    selectedTaxMode ===
                    TaxForecastProfileTaxCalculationMode.Standard
                  }
                />
              </SunshineCard.RadioGroup>
            )}
          />
        </FormProvider>
      </StepLayout.Body>

      <StepLayout.Sidebar>
        <Heading marginBottom="space-16">{locales.sidebar.title}</Heading>

        <Accordion allowMultiple allowToggle={undefined}>
          <Accordion.Item title={locales.sidebar.differentRates.question}>
            <Accordion.Text>
              {locales.sidebar.differentRates.threeTaxes}

              <UnorderedList>
                <ListItem>
                  <Text variant="light">
                    {locales.sidebar.differentRates.personalized}
                  </Text>
                </ListItem>

                <ListItem>
                  <Text variant="light">
                    {locales.sidebar.differentRates.neutral}
                  </Text>
                </ListItem>

                <ListItem>
                  <Text variant="light">
                    {locales.sidebar.differentRates.standard}
                  </Text>
                </ListItem>
              </UnorderedList>
            </Accordion.Text>
          </Accordion.Item>

          <Accordion.Item title={locales.sidebar.taxPersonalized.question}>
            <Accordion.Text>
              {locales.sidebar.taxPersonalized.info}
            </Accordion.Text>
          </Accordion.Item>

          <Accordion.Item title={locales.sidebar.taxNeutral.question}>
            <Accordion.Text>{locales.sidebar.taxNeutral.info}</Accordion.Text>
          </Accordion.Item>

          <Accordion.Item title={locales.sidebar.taxStandard.question}>
            <Accordion.Text>{locales.sidebar.taxStandard.info}</Accordion.Text>
          </Accordion.Item>
        </Accordion>
      </StepLayout.Sidebar>

      <StepLayout.Footer stepProgress={stepProgress}>
        <StepLayout.FooterButton
          isLoading={loading}
          onClick={form.handleSubmit(handleSave)}
        />
      </StepLayout.Footer>
    </StepLayout>
  );
};

export default TaxType;
